/**
 * Global floating action buttons: WhatsApp and Go to Top.
 * Rendered via React portal directly into document.body (no wrapper div) so they
 * are never trapped by app layout, transform, overflow, or stacking context.
 * Uses a plain div with inline styles for the container to avoid any MUI/Emotion
 * or theme affecting fixed positioning.
 */

import { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import { useMaterialUIController } from "context";
import { contactConfig } from "config/contact";

const BRAND_GOLD = "#b6925b";
const Z_INDEX = 2147483647;

function getWhatsAppUrl(phone) {
  if (!phone) return "#";
  const digits = phone.replace(/\D/g, "");
  const withCountry = digits.startsWith("0") ? `20${digits.slice(1)}` : digits;
  return `https://wa.me/${withCountry}`;
}

function useIsSmallScreen() {
  const [small, setSmall] = useState(
    typeof window !== "undefined" && window.innerWidth <= 480
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 480px)");
    const handler = () => setSmall(mql.matches);
    mql.addEventListener("change", handler);
    handler();
    return () => mql.removeEventListener("change", handler);
  }, []);
  return small;
}

function FloatingButtons() {
  const [controller] = useMaterialUIController();
  const { direction } = controller;
  const isRTL = direction === "rtl";
  const isSmall = useIsSmallScreen();

  const [showGoToTop, setShowGoToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowGoToTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const whatsappUrl = getWhatsAppUrl(contactConfig.supportPhone);
  const tooltipPlacement = isRTL ? "right" : "left";

  const wrapperStyle = useMemo(
    () => ({
      position: "fixed",
      // On mobile, sit above the footer so we don't overlap footer text
      bottom: isSmall ? 72 : 24,
      ...(isRTL
        ? { left: isSmall ? 16 : 24, right: "auto" }
        : { right: isSmall ? 16 : 24, left: "auto" }),
      zIndex: Z_INDEX,
      display: "flex",
      flexDirection: "column",
      gap: isSmall ? 10 : 12,
      pointerEvents: "none",
    }),
    [isRTL, isSmall]
  );

  const buttonCommonSx = {
    pointerEvents: "auto",
    boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
    "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.25)" },
    ...(isSmall && {
      width: 44,
      height: 44,
      minHeight: 44,
      "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
    }),
  };

  const floatingContent = (
    <div className="floating-actions" style={wrapperStyle}>
      {showGoToTop && (
        <Tooltip title="Go to top" placement={tooltipPlacement}>
          <Fab
            aria-label="Go to top"
            size="medium"
            onClick={scrollToTop}
            sx={{
              ...buttonCommonSx,
              backgroundColor: BRAND_GOLD,
              color: "#fff",
              "&:hover": { backgroundColor: "#a67d4d" },
            }}
          >
            <Icon>keyboard_arrow_up</Icon>
          </Fab>
        </Tooltip>
      )}
      <Tooltip title="Contact us on WhatsApp" placement={tooltipPlacement}>
        <Fab
          component="a"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          size="medium"
          sx={{
            ...buttonCommonSx,
            backgroundColor: "#25D366",
            color: "#fff",
            "&:hover": { backgroundColor: "#20BD5A" },
          }}
        >
          <Icon sx={{ fontSize: "1.5rem" }}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </Icon>
        </Fab>
      </Tooltip>
    </div>
  );

  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(floatingContent, document.body);
}

export default FloatingButtons;
